import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import axios from "axios";
import BusyButton from "../components/basic/busy-button";
import Checkbox from "../components/basic/checkbox";
import ConfirmDialog, {
  type ConfirmDialogProps,
} from "../components/basic/confirm-dialog";
import MigrationAlterDialog from "./MigrationAlterDialog";
import { apiHttpUrl } from "../helpers/api-base";
import { getAuthToken, withAuthToken } from "../helpers/auth";
import type { AccountRow } from "../params/types";

type Group = "avatar" | "photos" | "videos" | "reels";
type Action = "post" | "avatar";

interface MigrationFile {
  name: string;
  kind: "image" | "video";
  size: number;
}

interface Source {
  username: string;
  counts: Record<Group, number>;
}

const LOCAL_SOURCE = "local-files";

type SourceType = "downloaded" | "local";

const SOURCE_TYPES: { id: SourceType; label: string }[] = [
  { id: "downloaded", label: "Downloaded" },
  { id: "local", label: "Local files" },
];

const GROUP_DEFS: { id: Group; label: string }[] = [
  { id: "avatar", label: "Avatar" },
  { id: "photos", label: "Images" },
  { id: "videos", label: "Videos" },
  { id: "reels", label: "Reels" },
];

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fileUrl(
  username: string,
  group: Group,
  name: string,
  version = 0,
): string {
  return withAuthToken(
    apiHttpUrl(
      `/api/migration/file?username=${encodeURIComponent(username)}` +
        `&group=${group}&name=${encodeURIComponent(name)}&v=${version}`,
    ),
  );
}

/** Small cached preview (resized image / first video frame). */
function thumbUrl(
  username: string,
  group: Group,
  name: string,
  version = 0,
): string {
  return withAuthToken(
    apiHttpUrl(
      `/api/migration/thumb?username=${encodeURIComponent(username)}` +
        `&group=${group}&name=${encodeURIComponent(name)}&v=${version}`,
    ),
  );
}

interface MigrationUploaderProps {
  accounts?: AccountRow[];
  /** True while this tab is visible; refreshes the downloaded-accounts list. */
  active?: boolean;
  setError?: (message: string) => void;
}

export default function MigrationUploader({
  accounts = [],
  active = true,
  setError,
}: MigrationUploaderProps) {
  const [account, setAccount] = useState("");
  const [sourceType, setSourceType] = useState<SourceType>("downloaded");
  const [downloadedSource, setDownloadedSource] = useState("");
  const source = sourceType === "local" ? LOCAL_SOURCE : downloadedSource;
  const sourceLabel = sourceType === "local" ? "local files" : `@${source}`;
  const [dragging, setDragging] = useState(false);
  const [adding, setAdding] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sources, setSources] = useState<Source[]>([]);
  const [groups, setGroups] = useState<Record<Group, MigrationFile[]> | null>(
    null,
  );
  /** Selected posts, as "group/name". Avatar-group files aren't selectable. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /**
   * Carousel posts: each entry is the "group/name" keys that publish together
   * as one post, in order. Anything not listed publishes as its own post.
   */
  const [postGroups, setPostGroups] = useState<string[][]>([]);
  const [busy, setBusy] = useState(false);
  const [alterOpen, setAlterOpen] = useState(false);
  const [confirm, setConfirm] = useState<Omit<
    ConfirmDialogProps,
    "onCancel"
  > | null>(null);
  /** Bumped after files change so thumbnails reload. */
  const [version, setVersion] = useState(0);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!active) return;
    axios
      .get(apiHttpUrl("/api/migration/sources"), { headers: authHeaders() })
      .then((r) => setSources(r.data?.sources || []))
      .catch(() => {});
  }, [active]);

  /** Files already published to the chosen account ("group/name"). */
  const [uploadedKeys, setUploadedKeys] = useState<Set<string>>(new Set());

  const loadUploaded = useCallback(async () => {
    if (!source || !account) {
      setUploadedKeys(new Set());
      return;
    }
    try {
      const r = await axios.get(apiHttpUrl("/api/migration/uploaded"), {
        headers: authHeaders(),
        params: { source, account },
      });
      setUploadedKeys(new Set<string>(r.data?.uploaded || []));
    } catch {
      /* the marks are a convenience */
    }
  }, [source, account]);

  // Refresh the marks on change, and while an upload runs so they appear live.
  useEffect(() => {
    if (!active) return;
    void loadUploaded();
    if (!busy) return;
    const t = setInterval(() => void loadUploaded(), 5000);
    return () => clearInterval(t);
  }, [active, busy, loadUploaded]);

  const loadGroups = useCallback(async () => {
    if (!source) return;
    try {
      const r = await axios.get(apiHttpUrl("/api/migration/list"), {
        headers: authHeaders(),
        params: { username: source },
      });
      setGroups(r.data?.groups || null);
    } catch {
      setError?.("Unable to load the files.");
    }
  }, [source, setError]);

  useEffect(() => {
    void loadGroups();
  }, [loadGroups]);

  /** Forget the current view when the source changes. */
  const resetView = () => {
    setGroups(null);
    setSelected(new Set());
    setPostGroups([]);
    setMessage("");
  };

  const addLocalFiles = async (files: File[]) => {
    if (!files.length || adding || busy) return;
    setAdding(true);
    setMessage("");
    const added: string[] = [];
    const failed: string[] = [];
    for (const file of files) {
      try {
        const res = await axios.post(apiHttpUrl("/api/migration/local"), file, {
          headers: {
            ...authHeaders(),
            "Content-Type": "application/octet-stream",
          },
          params: { name: file.name },
          validateStatus: () => true,
          timeout: 0,
        });
        if (res.data?.ok) added.push(`${res.data.group}/${res.data.name}`);
        else failed.push(`${file.name}: ${res.data?.error || "failed"}`);
      } catch {
        failed.push(`${file.name}: could not be sent`);
      }
    }
    await loadGroups();
    setSelected((p) => new Set([...p, ...added]));
    setVersion((v) => v + 1);
    setMessage(
      `Added ${added.length} file(s).` +
        (failed.length ? ` ${failed.length} skipped: ${failed[0]}` : ""),
    );
    setAdding(false);
  };

  const clearLocal = async () => {
    setConfirm({
      title: "Remove added files?",
      note: "Only the copies added here are removed. Your originals on this PC are not touched.",
      confirmLabel: "Remove",
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await axios.delete(apiHttpUrl("/api/migration/local"), {
            headers: authHeaders(),
          });
          resetView();
          await loadGroups();
        } catch {
          setError?.("Unable to reach the server.");
        }
      },
    });
  };

  const selectableKeys = GROUP_DEFS.filter((g) => g.id !== "avatar").flatMap(
    (g) => (groups?.[g.id] || []).map((f) => `${g.id}/${f.name}`),
  );
  const selectedCount = selectableKeys.filter((k) => selected.has(k)).length;
  const allSelected =
    selectableKeys.length > 0 && selectedCount === selectableKeys.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const formValid = Boolean(account) && Boolean(source) && selectedCount > 0;

  // Drop files that disappeared (removed, cleared) from their groups; a group
  // needs at least two files to stay a carousel.
  useEffect(() => {
    if (!groups) return;
    const exist = new Set(
      GROUP_DEFS.flatMap((g) =>
        (groups[g.id] || []).map((f) => `${g.id}/${f.name}`),
      ),
    );
    setPostGroups((prev) => {
      const next = prev
        .map((g) => g.filter((k) => exist.has(k)))
        .filter((g) => g.length >= 2);
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, [groups]);

  const groupIndexOf = (key: string) =>
    postGroups.findIndex((g) => g.includes(key));

  /** Ticking a grouped file ticks its whole group, so a carousel stays whole. */
  const toggle = (key: string, on: boolean) =>
    setSelected((p) => {
      const next = new Set(p);
      const gi = postGroups.findIndex((g) => g.includes(key));
      for (const k of gi >= 0 ? postGroups[gi] : [key]) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });

  /** Tick/untick many files at once; carousels still move as a whole. */
  const setMany = (keys: string[], on: boolean) =>
    setSelected((p) => {
      const next = new Set(p);
      for (const key of keys) {
        const gi = postGroups.findIndex((g) => g.includes(key));
        for (const k of gi >= 0 ? postGroups[gi] : [key]) {
          if (on) next.add(k);
          else next.delete(k);
        }
      }
      return next;
    });

  const selectedKeys = [...selected].filter((k) => selectableKeys.includes(k));
  const ungroupedSelected = selectedKeys.filter((k) => groupIndexOf(k) < 0);
  const canGroup =
    ungroupedSelected.length >= 2 && ungroupedSelected.length <= 10;
  const canUngroup = selectedKeys.some((k) => groupIndexOf(k) >= 0);

  const onGroup = () => {
    if (!canGroup) return;
    setPostGroups((p) => [...p, ungroupedSelected]);
    // Ready to pick the next group; tick the group again to publish it.
    setSelected(new Set());
  };

  const onUngroup = () => {
    const sel = new Set(selectedKeys);
    setPostGroups((p) => p.filter((g) => !g.some((k) => sel.has(k))));
  };

  // "G" does what the Group / Ungroup button does (not while typing, or with
  // a dialog open, or on another tab).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "g" || e.ctrlKey || e.metaKey || e.altKey) return;
      if (!active || busy || confirm || alterOpen) return;
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) {
        // Tile and select-all checkboxes are inputs too; let those through.
        if (!(el instanceof HTMLInputElement && el.type === "checkbox")) return;
      }
      if (canGroup) onGroup();
      else if (canUngroup) onUngroup();
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const toItem = (k: string) => {
    const [group, ...rest] = k.split("/");
    return { group: group as Group, name: rest.join("/") };
  };

  /** Selected files as posts: each carousel once, every other file alone. */
  const buildPosts = (): string[][] => {
    const posts: string[][] = [];
    const done = new Set<number>();
    for (const k of selectedKeys) {
      const gi = groupIndexOf(k);
      if (gi < 0) posts.push([k]);
      else if (!done.has(gi)) {
        done.add(gi);
        posts.push(postGroups[gi]);
      }
    }
    return posts;
  };

  const send = async (
    payload:
      | { groups: { group: Group; name: string }[][] }
      | { items: { group: Group; name: string }[] },
    action: Action,
  ) => {
    setBusy(true);
    setMessage("");
    try {
      const res = await axios.post(
        apiHttpUrl("/api/migration/upload"),
        { account, source, action, ...payload },
        { headers: authHeaders(), validateStatus: () => true, timeout: 0 },
      );
      const data = res.data || {};
      if (data.error) setError?.(data.error);
      const uploaded: string[] = data.uploaded || [];
      const failed: string[] = data.failed || [];
      setMessage(
        `Uploaded ${uploaded.length} item(s).` +
          (failed.length ? ` ${failed.length} failed: ${failed[0]}` : ""),
      );
      if (action === "post") {
        setSelected((p) => {
          const next = new Set(p);
          uploaded.forEach((k) => next.delete(k));
          return next;
        });
      }
    } catch {
      setError?.("Unable to reach the server.");
    } finally {
      setBusy(false);
    }
  };

  const selectedItems = selectedKeys.map(toItem);

  const onUpload = () => {
    if (!formValid || busy) return;
    const posts = buildPosts();
    const carousels = posts.filter((p) => p.length > 1).length;
    setConfirm({
      title: "Publish to Instagram?",
      rows: [
        { label: "Posts", value: posts.length },
        ...(carousels > 0
          ? [{ label: "Multi-photo posts", value: carousels }]
          : []),
        { label: "Items", value: selectedKeys.length },
        { label: "From", value: sourceLabel },
        { label: "Upload to", value: account },
      ],
      note: "This posts publicly on Instagram.",
      confirmLabel: "Upload",
      onConfirm: () => {
        setConfirm(null);
        void send({ groups: posts.map((p) => p.map(toItem)) }, "post");
      },
    });
  };

  const onRemove = () => {
    if (!source || selectedItems.length === 0 || busy) return;
    const items = selectedItems;
    setConfirm({
      title: "Remove selected files?",
      rows: [
        { label: "Files", value: items.length },
        { label: "From", value: sourceLabel },
      ],
      note:
        sourceType === "local"
          ? "Only the copies added here are deleted. Your originals on this PC are not touched."
          : `This permanently deletes the files from downloads/${source}.`,
      confirmLabel: "Remove",
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try {
          const res = await axios.post(
            apiHttpUrl("/api/migration/remove"),
            { source, items },
            { headers: authHeaders(), validateStatus: () => true },
          );
          const removed: string[] = res.data?.removed || [];
          const failed: string[] = res.data?.failed || [];
          if (res.data?.error) setError?.(res.data.error);
          setSelected((p) => {
            const next = new Set(p);
            removed.forEach((k) => next.delete(k));
            return next;
          });
          await loadGroups();
          setMessage(
            `Removed ${removed.length} file(s).` +
              (failed.length ? ` ${failed.length} failed: ${failed[0]}` : ""),
          );
        } catch {
          setError?.("Unable to reach the server.");
        }
      },
    });
  };

  const onAvatar = (group: Group, file: MigrationFile) => {
    if (!account || busy) return;
    setConfirm({
      title: "Change the avatar?",
      rows: [
        { label: "Image", value: file.name },
        { label: "Upload to", value: account },
      ],
      note: "This replaces the profile picture on Instagram.",
      confirmLabel: "Set avatar",
      onConfirm: () => {
        setConfirm(null);
        void send({ items: [{ group, name: file.name }] }, "avatar");
      },
    });
  };

  return (
    <div className="ib-migration-view">
      <div className="ib-settings-section ib-migration-card">
        <div className="ib-migration-bar">
          <label className="ib-migration-limit">
            <span>Upload to</span>
            <select
              className="ib-migration-select"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              disabled={busy}
            >
              <option value="">Select…</option>
              {accounts
                .filter((a) => a.session_status !== "banned")
                .map((a) => (
                  <option key={a.username} value={a.username}>
                    {a.username}
                    {a.ig_username ? ` (@${a.ig_username})` : ""}
                  </option>
                ))}
            </select>
          </label>
          <label className="ib-migration-limit">
            <span>Source type</span>
            <select
              className="ib-migration-select"
              value={sourceType}
              onChange={(e) => {
                setSourceType(e.target.value as SourceType);
                resetView();
              }}
              disabled={busy || adding}
            >
              {SOURCE_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          {sourceType === "downloaded" && (
            <label className="ib-migration-limit">
              <span>Source</span>
              <select
                className="ib-migration-select"
                value={downloadedSource}
                onChange={(e) => {
                  setDownloadedSource(e.target.value);
                  resetView();
                }}
                disabled={busy}
              >
                <option value="">Select downloaded account…</option>
                {sources.map((s) => (
                  <option key={s.username} value={s.username}>
                    @{s.username}
                  </option>
                ))}
              </select>
            </label>
          )}
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            disabled={busy || selectableKeys.length === 0}
            label={
              selectedCount > 0 ? `${selectedCount} selected` : "Select all"
            }
            onChange={(on) =>
              setSelected(on ? new Set(selectableKeys) : new Set())
            }
          />
          <BusyButton
            variant="jf-btn jf-btn-primary ib-migration-btn"
            label="Upload"
            busy={busy}
            busyLabel="Uploading…"
            disabled={!formValid}
            onClick={onUpload}
          />
          <button
            type="button"
            className="jf-btn jf-btn-primary-outline ib-migration-btn"
            disabled={busy || !(canGroup || canUngroup)}
            title={
              canUngroup && !canGroup
                ? "Split the selected multi-photo posts apart"
                : "Publish the selected files together as one multi-photo post (2-10)"
            }
            onClick={canGroup ? onGroup : onUngroup}
          >
            {canGroup ? "Group" : "Ungroup"}
          </button>
          <button
            type="button"
            className="jf-btn jf-btn-primary ib-migration-btn"
            disabled={!source || selectedCount === 0 || busy}
            onClick={() => setAlterOpen(true)}
          >
            Alter
          </button>
          <button
            type="button"
            className="jf-btn jf-btn-danger-outline ib-migration-btn"
            disabled={!source || selectedCount === 0 || busy}
            onClick={onRemove}
          >
            Remove
          </button>
        </div>
        {sourceType === "local" && (
          <div
            className={`ib-dropzone${dragging ? " ib-dropzone--active" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              void addLocalFiles(Array.from(e.dataTransfer.files));
            }}
          >
            <span>
              {adding
                ? "Adding files…"
                : "Drag and drop images or videos here, or"}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              accept="image/*,video/mp4,video/quicktime,.jpg,.jpeg,.png,.webp,.mp4,.mov"
              onChange={(e) => {
                void addLocalFiles(Array.from(e.target.files || []));
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="jf-btn jf-btn-primary-outline ib-migration-btn"
              disabled={adding || busy}
              onClick={() => fileInputRef.current?.click()}
            >
              Select files
            </button>
            {selectableKeys.length > 0 && (
              <button
                type="button"
                className="jf-btn jf-btn-primary-outline ib-migration-btn"
                disabled={adding || busy}
                onClick={clearLocal}
              >
                Clear
              </button>
            )}
          </div>
        )}
        {busy && (
          <p className="ib-migration-meta">
            Uploading… this opens the account&apos;s browser and can take
            several minutes.
          </p>
        )}
        {message && <p className="ib-migration-meta">{message}</p>}
        {!account && source && (
          <p className="ib-migration-meta">
            Choose where to upload (Upload to) to enable uploading.
          </p>
        )}
      </div>

      {groups &&
        GROUP_DEFS.map(({ id, label }) => {
          const files = groups[id] || [];
          const keys = files.map((f) => `${id}/${f.name}`);
          const picked = keys.filter((k) => selected.has(k)).length;
          return (
            <section key={id} className="ib-migration-group">
              <h3 className="ib-migration-group__title">
                {label} <span>({files.length})</span>
              </h3>
              {id !== "avatar" && files.length > 0 && (
                <Checkbox
                  className="ib-migration-group__all"
                  checked={picked === keys.length}
                  indeterminate={picked > 0 && picked < keys.length}
                  disabled={busy}
                  label={picked > 0 ? `${picked} selected` : "Select all"}
                  onChange={(on) => setMany(keys, on)}
                />
              )}
              {files.length === 0 ? (
                <p className="ib-migration-empty">Nothing found.</p>
              ) : (
                <div className="ib-migration-grid">
                  {files.map((f) => {
                    const src = fileUrl(source, id, f.name, version);
                    const key = `${id}/${f.name}`;
                    const canAvatar =
                      id === "avatar" ||
                      (id === "photos" && f.kind === "image");
                    const gi = groupIndexOf(key);
                    const pos = gi >= 0 ? postGroups[gi].indexOf(key) + 1 : 0;
                    return (
                      <div
                        key={f.name}
                        className={`ib-migration-card-item${
                          gi >= 0 ? " ib-migration-card-item--grouped" : ""
                        }`}
                        style={
                          gi >= 0
                            ? ({
                                "--ib-group-hue": (gi * 67 + 255) % 360,
                              } as CSSProperties)
                            : undefined
                        }
                      >
                        {gi >= 0 && (
                          <span
                            className="ib-group-badge"
                            title={`Post ${gi + 1}: item ${pos} of ${postGroups[gi].length}`}
                          >
                            {gi + 1}.{pos}
                          </span>
                        )}
                        {id !== "avatar" && uploadedKeys.has(key) && (
                          <span
                            className="ib-uploaded-badge"
                            title={`Already uploaded to ${account}`}
                          >
                            ✓ Uploaded
                          </span>
                        )}
                        {id !== "avatar" && (
                          <Checkbox
                            className="ib-checkbox--tile"
                            checked={selected.has(key)}
                            disabled={busy}
                            title={f.name}
                            onChange={(on) => toggle(key, on)}
                          />
                        )}
                        <a
                          className="ib-migration-item"
                          href={src}
                          target="_blank"
                          rel="noreferrer"
                          title={f.name}
                        >
                          <img
                            src={thumbUrl(source, id, f.name, version)}
                            alt=""
                            loading="lazy"
                            decoding="async"
                          />
                          {f.kind === "video" && (
                            <span className="ib-play" aria-hidden="true" />
                          )}
                        </a>
                        {canAvatar && (
                          <div className="ib-migration-item-actions">
                            <button
                              type="button"
                              className="jf-btn jf-btn-primary ib-migration-btn ib-migration-btn--sm"
                              disabled={!account || busy}
                              onClick={() => onAvatar(id, f)}
                            >
                              {id === "avatar" ? "Set avatar" : "Avatar"}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      {confirm && (
        <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />
      )}
      {alterOpen && (
        <MigrationAlterDialog
          source={source}
          items={selectedItems}
          setError={setError}
          onClose={() => setAlterOpen(false)}
          onAltered={() => setVersion((v) => v + 1)}
        />
      )}
    </div>
  );
}
